import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  // { code: 'ar', label: 'العربية' },
  { code: 'fr', label: 'Français' },
];

const Login = () => {
  const emailRef = useRef();
  const passwordRef = useRef();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { lang, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Read directly from DOM — fixes mobile autofill bypassing React state
    const form = e.target;
    const email = (
      emailRef.current?.value ||
      form.elements.email?.value ||
      ''
    ).trim().toLowerCase();

    const password = (
      passwordRef.current?.value ||
      form.elements.password?.value ||
      ''
    );

    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 15000 });
      const data = res.data;

      // Store token and redirect based on role
      const role = data.role || 'patient';
      try {
        if (role === 'admin') {
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.admin));
        } else {
          localStorage.setItem('token', data.token);
        }
      } catch {}

      toast.success(t.loginSuccess || 'Login successful!');
      window.location.href = role === 'admin' ? '/admin/dashboard' : '/portal/dashboard';
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please check your internet connection and try again.');
      } else if (err.response) {
        setError(err.response.data?.detail || 'Login failed. Please check your credentials.');
      } else {
        setError('Unable to connect to server. Please check your internet and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" dir={t.dir}>
      <div className="hidden lg:block lg:w-1/2 relative bg-[#2C3E50]">
        <img src="https://customer-assets.emergentagent.com/job_clinic-portal-51/artifacts/syczyims_IMG_7473.jpeg" alt="Younes Dental Clinic Logo" className="absolute inset-0 w-full h-full object-contain p-16" />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-[#0F4C5C] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>{t.clinicName}</h2>
            <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.welcomeBack}</h1>
            <p className="text-[#6B7280]">{t.signInSubtitle}</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="mb-5">
              <label htmlFor="login-email" className="block text-sm font-medium text-[#1F2937] mb-2">{t.email}</label>
              <input
                ref={emailRef}
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                inputMode="email"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]"
                placeholder={t.emailPlaceholder}
                data-testid="login-email-input"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="login-password" className="block text-sm font-medium text-[#1F2937] mb-2">{t.password}</label>
              <div className="relative">
                <input
                  ref={passwordRef}
                  id="login-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-[16px]"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937]" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">{error}</div>
            )}

            <button type="submit" disabled={loading} className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-[16px]" data-testid="login-submit-button">
              {loading ? t.signingIn : t.signIn}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-[#6B7280] hover:text-[#0F4C5C] transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-[#6B7280]">
              {t.noAccount}{' '}
              <Link to="/register" className="text-[#0F4C5C] hover:text-[#0F4C5C]/80 font-semibold" data-testid="register-link">{t.registerHere}</Link>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-1" data-testid="language-selector">
            <Globe className="w-4 h-4 text-[#6B7280]" />
            {LANG_OPTIONS.map((opt, i) => (
              <React.Fragment key={opt.code}>
                {i > 0 && <span className="text-gray-300">|</span>}
                <button onClick={() => setLanguage(opt.code)} className={`px-2 py-1 text-sm rounded transition-colors ${lang === opt.code ? 'text-[#0F4C5C] font-semibold' : 'text-[#6B7280] hover:text-[#1F2937]'}`} data-testid={`lang-${opt.code}`}>
                  {opt.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
